import {Size} from '@gl/lit/components/base/icon/index.js';
import type {
  NodeClassType,
  BuilderEdgeTypes,
  EdgeClassType,
  IClassicBuilderEdge,
  IClassicBuilderNode,
} from '@gl/builder';
import type {ExpressionOperatorDisplay} from '@gl/lit/types/builders/audience_builder/index.js';
import {
  AudienceBuilderNodeTypes,
  ExpressionOperators,
  ExpressionTypes,
  FilterOperators,
  InputExpressionTypes,
  NeighborOperators,
} from '@gl/lit/types/builders/audience_builder/index.js';
import type {DatasetLabel} from '@gl/lit/types/datasets.js';
import {DatasetType, datasetTypeLabel} from '@gl/lit/types/datasets.js';
import type {DestinationConnection} from '@gl/types/destinations.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {isCSV} from '@gl/lit/utils/destinations.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {when} from 'lit/directives/when.js';

export const LAYER_ROOT_KEY = '__AB_ROOT__';
export const DEFAULT_AUDIENCE_NAME = 'Untitled Audience';
export const DEFAULT_TEMPLATE_NAME = 'Untitled Template';
export const NUMBER_EXPRESSION_PLACEHOLDER = 'e.g. 250';
/** Key used to store audience builder preferences. */
export const BUILDER_PREFERENCES_KEY = 'ab:preferences';
export const AUDIENCES_PATH = '/audiences';

/**
 * Creates an operator display object literal.
 */
function createOperatorDisplay(
  label: string,
  suffix = ''
): ExpressionOperatorDisplay {
  return {label, suffix};
}

export const audienceBuilderNodeMap = new Map<
  AudienceBuilderNodeTypes,
  NodeClassType<IClassicBuilderNode>
>([]);

export const abNodeLabelMap = new Map([
  [AudienceBuilderNodeTypes.FILTER, 'filter'],
  [AudienceBuilderNodeTypes.FLEX, 'dataset'],
  [AudienceBuilderNodeTypes.AUDIENCE, 'audience exclusion'],
]);

export const audienceBuilderEdgeMap = new Map<
  BuilderEdgeTypes,
  EdgeClassType<IClassicBuilderEdge>
>([]);

export const contentTypeNodes = [
  AudienceBuilderNodeTypes.TEXT,
  AudienceBuilderNodeTypes.HEADING,
];

export const expressionOperatorDisplayMap = new Map([
  [ExpressionOperators.EQUALS, createOperatorDisplay('is equal to')],
  [ExpressionOperators.DOES_NOT_EQUAL, createOperatorDisplay('does not equal')],
  [ExpressionOperators.IS_NULL, createOperatorDisplay('is null')],
  [ExpressionOperators.IS_NOT_NULL, createOperatorDisplay('is not null')],
  [ExpressionOperators.IS_EMPTY, createOperatorDisplay('is empty')],
  [ExpressionOperators.IS_NOT_EMPTY, createOperatorDisplay('is not empty')],
  [ExpressionOperators.GREATER_THAN, createOperatorDisplay('is greater than')],
  [ExpressionOperators.LESS_THAN, createOperatorDisplay('is less than')],
  [ExpressionOperators.BETWEEN, createOperatorDisplay('is between')],
  [ExpressionOperators.CONTAINS, createOperatorDisplay('contains')],
  [
    ExpressionOperators.DOES_NOT_CONTAIN,
    createOperatorDisplay('does not contain'),
  ],
  [ExpressionOperators.STARTS_WITH, createOperatorDisplay('starts with')],
  [
    ExpressionOperators.DOES_NOT_START_WITH,
    createOperatorDisplay('does not start with'),
  ],
  [ExpressionOperators.ENDS_WITH, createOperatorDisplay('ends with')],
  [
    ExpressionOperators.DOES_NOT_END_WITH,
    createOperatorDisplay('does not end with'),
  ],
  [
    ExpressionOperators.GREATER_THAN_OR_EQUAL,
    createOperatorDisplay('is greater than or equal to'),
  ],
  [
    ExpressionOperators.LESS_THAN_OR_EQUAL,
    createOperatorDisplay('is less than or equal to'),
  ],
  [
    ExpressionOperators.LENGTH_EQUALS,
    createOperatorDisplay('has a length equal to'),
  ],
  [
    ExpressionOperators.LENGTH_GREATER,
    createOperatorDisplay('has a length greater than'),
  ],
  [
    ExpressionOperators.LENGTH_LESS,
    createOperatorDisplay('has a length less than'),
  ],
]);

export const datetimeOperatorDisplayMap = new Map([
  ...expressionOperatorDisplayMap,
  [ExpressionOperators.GREATER_THAN, createOperatorDisplay('is after')],
  [ExpressionOperators.LESS_THAN, createOperatorDisplay('is before')],
  [
    ExpressionOperators.IN_LAST_DAYS,
    createOperatorDisplay('occurred within the last', 'days'),
  ],
  [
    ExpressionOperators.OVER_DAYS_AGO,
    createOperatorDisplay('happened over', 'days ago'),
  ],
  [
    ExpressionOperators.BETWEEN_DAYS_AGO,
    createOperatorDisplay('occurred between the last', 'days'),
  ],
  [
    ExpressionOperators.ON_DAYS_AGO,
    createOperatorDisplay('happened exactly', 'days ago'),
  ],
  [
    ExpressionOperators.IN_NEXT_DAYS,
    createOperatorDisplay('will occur within the next', 'days'),
  ],
  [
    ExpressionOperators.OVER_DAYS_FROM_NOW,
    createOperatorDisplay('will happen over', 'days from now'),
  ],
  [
    ExpressionOperators.BETWEEN_DAYS_FROM_NOW,
    createOperatorDisplay('will occur between the next', 'days'),
  ],
  [
    ExpressionOperators.ON_DAYS_FROM_NOW,
    createOperatorDisplay('will happen exactly', 'days from now'),
  ],
  [ExpressionOperators.IS_TODAY, createOperatorDisplay('is today')],
  [ExpressionOperators.IS_YESTERDAY, createOperatorDisplay('is yesterday')],
  [ExpressionOperators.IS_TOMORROW, createOperatorDisplay('is tomorrow')],
]);

export const daysOperators = [
  ExpressionOperators.IN_LAST_DAYS,
  ExpressionOperators.OVER_DAYS_AGO,
  ExpressionOperators.BETWEEN_DAYS_AGO,
  ExpressionOperators.ON_DAYS_AGO,
  ExpressionOperators.IN_NEXT_DAYS,
  ExpressionOperators.OVER_DAYS_FROM_NOW,
  ExpressionOperators.BETWEEN_DAYS_FROM_NOW,
  ExpressionOperators.ON_DAYS_FROM_NOW,
];

export const betweenOperators = [
  ExpressionOperators.BETWEEN,
  ExpressionOperators.BETWEEN_DAYS_AGO,
  ExpressionOperators.BETWEEN_DAYS_FROM_NOW,
];

export const nullOperators = [
  ExpressionOperators.IS_NULL,
  ExpressionOperators.IS_NOT_NULL,
  ExpressionOperators.IS_EMPTY,
  ExpressionOperators.IS_NOT_EMPTY,
  ExpressionOperators.IS_TOMORROW,
  ExpressionOperators.IS_YESTERDAY,
  ExpressionOperators.IS_TODAY,
];

export const numericArrayOperators = [
  ExpressionOperators.LENGTH_LESS,
  ExpressionOperators.LENGTH_GREATER,
  ExpressionOperators.LENGTH_EQUALS,
];

export const validStringOperators = [
  ExpressionOperators.EQUALS,
  ExpressionOperators.DOES_NOT_EQUAL,
  ExpressionOperators.IS_NULL,
  ExpressionOperators.IS_NOT_NULL,
  ExpressionOperators.CONTAINS,
  ExpressionOperators.DOES_NOT_CONTAIN,
  ExpressionOperators.STARTS_WITH,
  ExpressionOperators.DOES_NOT_START_WITH,
  ExpressionOperators.ENDS_WITH,
  ExpressionOperators.DOES_NOT_END_WITH,
];

export const validNumberOperators = [
  ExpressionOperators.EQUALS,
  ExpressionOperators.GREATER_THAN,
  ExpressionOperators.LESS_THAN,
  ExpressionOperators.GREATER_THAN_OR_EQUAL,
  ExpressionOperators.LESS_THAN_OR_EQUAL,
  ExpressionOperators.BETWEEN,
  ExpressionOperators.IS_NULL,
  ExpressionOperators.IS_NOT_NULL,
];

export const validDatetimeOperators = [
  ExpressionOperators.IS_TODAY,
  ExpressionOperators.IS_TOMORROW,
  ExpressionOperators.IS_YESTERDAY,
  ExpressionOperators.EQUALS, // Might have to remove depending on Quervice support
  ExpressionOperators.DOES_NOT_EQUAL, // Might have to remove depending on Quervice support
  ExpressionOperators.IS_NULL,
  ExpressionOperators.IS_NOT_NULL,
  ExpressionOperators.GREATER_THAN, // i.e., "After date"
  ExpressionOperators.LESS_THAN, // i.e., "Before date"
  ExpressionOperators.BETWEEN,
  ExpressionOperators.IN_LAST_DAYS,
  ExpressionOperators.OVER_DAYS_AGO,
  ExpressionOperators.BETWEEN_DAYS_AGO,
  ExpressionOperators.ON_DAYS_AGO,
  ExpressionOperators.IN_NEXT_DAYS,
  ExpressionOperators.OVER_DAYS_FROM_NOW,
  ExpressionOperators.BETWEEN_DAYS_FROM_NOW,
  ExpressionOperators.ON_DAYS_FROM_NOW,
];

export const validArrayOperators = [
  ExpressionOperators.EQUALS,
  ExpressionOperators.DOES_NOT_EQUAL,
  ExpressionOperators.IS_EMPTY,
  ExpressionOperators.IS_NOT_EMPTY,
  ExpressionOperators.LENGTH_EQUALS,
  ExpressionOperators.LENGTH_GREATER,
  ExpressionOperators.LENGTH_LESS,
];

export const datetimeExpressionTypes = [
  ExpressionTypes.DATETIME,
  ExpressionTypes.TIMESTAMP,
  ExpressionTypes.DATE,
];

export const floatExpressionTypes = [
  ExpressionTypes.FLOAT,
  ExpressionTypes.FLOAT_64,
  ExpressionTypes.NUMERIC,
];

export const integerExpressionTypes = [
  ExpressionTypes.NUMBER,
  ExpressionTypes.INT,
  ExpressionTypes.INT_64,
  ExpressionTypes.INTEGER,
  ExpressionTypes.INTEGER_64,
];

export const numericExpressionTypes: ExpressionTypes[] = [
  ...integerExpressionTypes,
  ...floatExpressionTypes,
];

export const booleanExpressionTypes = [
  ExpressionTypes.BOOL,
  ExpressionTypes.BOOLEAN,
];

export const neighborOperatorLabelMap = new Map([
  [NeighborOperators.AND, 'And'],
  [NeighborOperators.AND_NOT, 'And not'],
  [NeighborOperators.OR, 'Or'],
  [NeighborOperators.XOR, 'Exclusive or'],
]);

export const leadingNeighborOperatorLabels = new Map([
  [NeighborOperators.AND, 'Where'],
  [NeighborOperators.AND_NOT, 'Where not'],
]);

export const filterOperatorLabelMap = new Map([
  [FilterOperators.AND, 'And'],
  [FilterOperators.OR, 'Or'],
]);

export const stemmableNeighborOperators = [
  NeighborOperators.AND,
  NeighborOperators.OR,
  NeighborOperators.XOR,
];

export const stemmableTypes = [
  AudienceBuilderNodeTypes.FLEX,
  AudienceBuilderNodeTypes.STEM,
  AudienceBuilderNodeTypes.FILTER,
];

export const stemmableTypeMap = new Map<
  AudienceBuilderNodeTypes,
  AudienceBuilderNodeTypes[]
>(stemmableTypes.map(type => [type, stemmableTypes]));

export const conjuntiveNeighborOperators = [
  NeighborOperators.AND,
  NeighborOperators.AND_NOT,
];

export const conjuctiveOperatorMap = new Map([
  [AudienceBuilderNodeTypes.AUDIENCE, conjuntiveNeighborOperators],
  [AudienceBuilderNodeTypes.FILTER, conjuntiveNeighborOperators],
  [AudienceBuilderNodeTypes.STEM, conjuntiveNeighborOperators],
  [
    AudienceBuilderNodeTypes.FLEX,
    [NeighborOperators.AND, NeighborOperators.OR],
  ],
]);

export function getDatasetSetupDescriptor(
  datasetType: DatasetType
): TemplateResult {
  switch (datasetType) {
    case DatasetType.TRANSACTION:
      return html`a <span class="transactions">transactions</span> dataset`;
    case DatasetType.EVENT:
      return html`an <span class="events">events</span> dataset`;
    default: {
      const label = datasetTypeLabel.get(datasetType).toLowerCase();
      const className = label.replace(/\s/g, '-');
      return html`an <span class="${className}">${label}</span> dataset`;
    }
  }
}

export function getDatasetSetupAction(datasetType: DatasetType): string {
  switch (datasetType) {
    case DatasetType.TRANSACTION:
      return 'purchases';
    case DatasetType.EVENT:
      return 'event interactions';
    default: {
      return datasetTypeLabel.get(datasetType).toLowerCase();
    }
  }
}

export const nodesWithManagementDisabled = [
  AudienceBuilderNodeTypes.TEXT,
  AudienceBuilderNodeTypes.FILTER,
  AudienceBuilderNodeTypes.STEM,
  AudienceBuilderNodeTypes.FLEX,
  AudienceBuilderNodeTypes.AUDIENCE,
];

export const defaultFilterStemContraints = new Set([
  AudienceBuilderNodeTypes.FILTER,
  AudienceBuilderNodeTypes.FLEX,
]);

export const labelToExpressionTypeMap: Map<
  DatasetLabel['field_type'],
  Set<ExpressionTypes>
> = new Map([
  ['string', new Set([ExpressionTypes.STRING])],
  ['boolean', new Set(booleanExpressionTypes)],
  ['float', new Set(numericExpressionTypes)],
  ['integer', new Set(numericExpressionTypes)],
  ['date', new Set(datetimeExpressionTypes)],
  ['timestamp', new Set(datetimeExpressionTypes)],
]);

export const dayOperatorsInputExpressionTypes: Map<
  ExpressionOperators,
  InputExpressionTypes
> = new Map([
  [ExpressionOperators.IN_LAST_DAYS, InputExpressionTypes.NUMBER],
  [ExpressionOperators.OVER_DAYS_AGO, InputExpressionTypes.NUMBER],
  [ExpressionOperators.BETWEEN_DAYS_AGO, InputExpressionTypes.NUMBER],
  [ExpressionOperators.ON_DAYS_AGO, InputExpressionTypes.NUMBER],
  [ExpressionOperators.IN_NEXT_DAYS, InputExpressionTypes.NUMBER],
  [ExpressionOperators.OVER_DAYS_FROM_NOW, InputExpressionTypes.NUMBER],
  [ExpressionOperators.BETWEEN_DAYS_FROM_NOW, InputExpressionTypes.NUMBER],
  [ExpressionOperators.ON_DAYS_FROM_NOW, InputExpressionTypes.NUMBER],
]);

export const dateOperatorsInputExpressionTypes: Map<
  ExpressionOperators,
  InputExpressionTypes
> = new Map([
  [ExpressionOperators.GREATER_THAN, InputExpressionTypes.DATE], // i.e., "After date"
  [ExpressionOperators.LESS_THAN, InputExpressionTypes.DATE],
  [ExpressionOperators.BETWEEN, InputExpressionTypes.DATE],
]);

export const nullOperatorsInputExpressionTypes: Map<
  ExpressionOperators,
  InputExpressionTypes
> = new Map(
  nullOperators.map(operator => [operator, InputExpressionTypes.NULL])
);

export const inputExpressionTypeMap: Map<
  ExpressionOperators,
  InputExpressionTypes
> = new Map([
  ...dateOperatorsInputExpressionTypes,
  ...dayOperatorsInputExpressionTypes,
  ...nullOperatorsInputExpressionTypes,
]);

/**
 * A list of prefixes of types sent via Querivce to standardize into
 * the 'NUMERIC' expression type.
 */
export const numericTypePrefixes: Set<string> = new Set([
  ExpressionTypes.NUMERIC,
  'BIGNUMERIC',
  'DECIMAL',
]);

export const MAX_CSV_SIZE = 500000;

export const COMPARISON_AUDIENCE_REPORTS_TAG_VALUE =
  'ab-comparisons-audience-reports';

/**
 * Map of available global events for the audience comparisons report
 * component to respond to.
 */
export const comparisonReportsGlobalEvents = Object.freeze({
  ADD: `${COMPARISON_AUDIENCE_REPORTS_TAG_VALUE}:add`,
  SET: `${COMPARISON_AUDIENCE_REPORTS_TAG_VALUE}:set`,
  REMOVE: `${COMPARISON_AUDIENCE_REPORTS_TAG_VALUE}:remove`,
});

export const renderSelectedDestinationHeaderIcon = (
  destination: Nullable<DestinationConnection>
): TemplateResult => {
  return when(destination, () => {
    return html`
      <div slot="header-label-prefix">
        <div class="header-prefix">
          <fly-icon
            svgIconSize="32px"
            ?svg=${!isCSV(destination)}
            .size=${Size.MD}
            .icon=${isCSV(destination) ? 'download' : (destination?.icon ?? '')}
          ></fly-icon>
        </div>
      </div>
    `;
  });
};
